import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MarketLayout from '../components/MarketLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_TABS = [
  { key: 'all',        label: 'All' },
  { key: 'staging',    label: 'Staging' },
  { key: 'published',  label: 'Published' },
  { key: 'bidding',    label: 'Bidding' },
  { key: 'under_loi',  label: 'Under LOI' },
  { key: 'closed',     label: 'Closed' },
];

const STATUS_STYLE = {
  staging:   { bg: '#f3f4f6', color: '#374151' },
  published: { bg: '#eff4ff', color: '#3a6ad9' },
  bidding:   { bg: '#f0fde4', color: '#111111' },
  under_loi: { bg: '#fffde4', color: '#111111' },
  closed:    { bg: '#1f2937', color: '#f9fafb' },
  withdrawn: { bg: '#ffe6e9', color: '#ff082f' },
};

function fmtUpb(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || { bg: '#f3f4f6', color: '#374151' };
  const display = (status || '—').replace(/_/g, ' ');
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 9999,
      backgroundColor: s.bg,
      color: s.color,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'capitalize',
    }}>{display}</span>
  );
}

export default function MarketPools() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/market/pools`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPools(res.data.pools || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load pools.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(
    () => (tab === 'all' ? pools : pools.filter((p) => p.status === tab)),
    [pools, tab]
  );

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/market/pools`,
        { poolName: createName.trim() || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newId = res.data.pool?.id;
      setShowCreate(false);
      setCreateName('');
      if (newId) navigate(`/admin/market/pools/${newId}`);
      else load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create pool.');
    } finally {
      setCreating(false);
    }
  };

  const handleQuickStatus = async (pool, status) => {
    if (status === 'published' && pool.loan_count < 1) {
      alert('Pool must contain at least one loan before publishing.');
      return;
    }
    setBusyId(pool.id);
    try {
      await axios.patch(
        `${API_URL}/api/admin/market/pools/${pool.id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } catch (err) {
      alert(err.response?.data?.error || `Failed to set ${status}.`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <MarketLayout>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>Pools</h1>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
              Stage, publish, and manage DoorlyMarket loan pools.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            style={btnPrimary}
          >
            + Create New Pool
          </button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: tab === t.key ? '#3a6ad9' : '#6b7280',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: tab === t.key ? '2px solid #3a6ad9' : '2px solid transparent',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#ff082f', fontSize: 14 }}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
              {tab === 'all' ? 'No pools yet — create one to begin.' : `No ${tab} pools.`}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <Th>Pool</Th>
                  <Th>Status</Th>
                  <Th align="right">Loans</Th>
                  <Th align="right">Total UPB</Th>
                  <Th align="right">WA FICO</Th>
                  <Th align="right">WA Rate</Th>
                  <Th align="right">Bids</Th>
                  <Th>Bid Deadline</Th>
                  <Th>Published</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <Td>
                      <div
                        onClick={() => navigate(`/admin/market/pools/${p.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div style={{ fontWeight: 600, color: '#3a6ad9' }}>{p.pool_name}</div>
                        <div style={{ color: '#6b7280', fontSize: 12 }}>{p.pool_number}</div>
                      </div>
                    </Td>
                    <Td><StatusPill status={p.status} /></Td>
                    <Td align="right">{p.loan_count || 0}</Td>
                    <Td align="right">{fmtUpb(p.total_upb)}</Td>
                    <Td align="right">{p.wa_fico || '—'}</Td>
                    <Td align="right">{p.wa_note_rate ? `${Number(p.wa_note_rate).toFixed(2)}%` : '—'}</Td>
                    <Td align="right">{p.bid_count || 0}</Td>
                    <Td>{fmtDate(p.bid_deadline)}</Td>
                    <Td>{fmtDate(p.published_at)}</Td>
                    <Td align="right">
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button onClick={() => navigate(`/admin/market/pools/${p.id}`)} style={btnGhost}>Edit</button>
                        <button
                          onClick={() => navigate(`/admin/market/pools/${p.id}?tab=bids`)}
                          style={btnGhost}
                        >
                          Bids
                        </button>
                        {p.status === 'staging' && (
                          <button
                            disabled={busyId === p.id || (p.loan_count || 0) < 1}
                            onClick={() => handleQuickStatus(p, 'published')}
                            style={{ ...btnBlue, opacity: ((p.loan_count || 0) < 1 ? 0.5 : 1) }}
                          >
                            Publish
                          </button>
                        )}
                        {['published', 'bidding'].includes(p.status) && (
                          <button
                            disabled={busyId === p.id}
                            onClick={() => {
                              if (window.confirm(`Withdraw pool ${p.pool_number}? Investors will no longer see it.`)) {
                                handleQuickStatus(p, 'withdrawn');
                              }
                            }}
                            style={btnRed}
                          >
                            Withdraw
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreate && (
        <div style={modalBackdrop}>
          <div style={modalBox}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
              Create New Pool
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
              Pool numbers are auto-generated (DLY-POOL-XXX). You can rename later.
            </p>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Pool name (optional)</label>
            <input
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="e.g. Texas Performing Pool 2026-Q2"
              style={{
                width: '100%', height: 40, padding: '0 12px',
                marginTop: 6, marginBottom: 16, borderRadius: 8,
                border: '1px solid #e5e7eb', fontSize: 13, boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreate(false)} style={btnGhost} disabled={creating}>Cancel</button>
              <button onClick={handleCreate} style={btnPrimary} disabled={creating}>
                {creating ? 'Creating…' : 'Create Pool'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MarketLayout>
  );
}

const Th = ({ children, align = 'left' }) => (
  <th style={{ textAlign: align, padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{children}</th>
);
const Td = ({ children, align = 'left' }) => (
  <td style={{ padding: '12px 16px', textAlign: align, color: '#111827', verticalAlign: 'middle' }}>{children}</td>
);

const btnBase    = { fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer' };
const btnPrimary = { ...btnBase, backgroundColor: '#3a6ad9', color: '#fff', padding: '8px 16px' };
const btnBlue    = { ...btnBase, backgroundColor: '#3a6ad9', color: '#fff' };
const btnRed     = { ...btnBase, backgroundColor: '#ff082f', color: '#fff' };
const btnGhost   = { ...btnBase, backgroundColor: 'transparent', color: '#374151', border: '1px solid #e5e7eb' };

const modalBackdrop = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', zIndex: 80,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const modalBox = { backgroundColor: '#fff', borderRadius: 14, padding: 24, maxWidth: 480, width: '100%' };
